import { useRef, useMemo, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useStepper } from "../../../context/stepper-provider";
import { useNotification } from "../../../context/notification-provider";
import StepContentBox from "../components/shared/step-content-box";
import useEntitySelection from "../hooks/use-entity-selection";
import EntityListItem from "../components/entity/entity-list-item";
import ShowAllToggle from "../components/entity/show-all-toggle";
import EmptyEntityState from "../components/entity/empty-entity-state";
import createEntityMenuActions from "../utils/entity-menu-actions";
import { addIconsToEntities } from "../utils/entity-transformers";
import { DEFAULT_DISPLAY_LIMIT } from "../constants";
import { useDataCleaningContext } from "../context/data-cleaning-context";

const SelectEntityStep = () => {
  const { nextStep, completeStep, activeStep, isStepCompleted } = useStepper();
  const { showNotification } = useNotification();
  const { selection } = useDataCleaningContext();
  const hasAdvanced = useRef(false);
  const hasSelectedClient = isStepCompleted(0);
  const stepRef = useRef<HTMLDivElement>(null);

  const {
    entities: entityData,
    displayedEntities: displayedEntityData,
    showAll,
    isLoading,
    error,
    handleEntityToggle,
    handleClearSelection,
    handleShowAllToggle,
    refetch,
  } = useEntitySelection(hasSelectedClient);

  // Add icons to entities for display
  const entities = useMemo(() => addIconsToEntities(entityData), [entityData]);

  const displayedEntities = useMemo(
    () => addIconsToEntities(displayedEntityData),
    [displayedEntityData],
  );

  const handleEntitySelect = (id: string) => {
    handleEntityToggle(id);

    // Auto-advance to next step
    if (!hasAdvanced.current) {
      hasAdvanced.current = true;
      requestAnimationFrame(() => {
        completeStep(activeStep);
        nextStep();
      });
    }
  };

  const handleClear = () => {
    handleClearSelection();
    hasAdvanced.current = false;
  };

  const handleRefresh = () => {
    refetch();
    showNotification({
      message: "Entities refreshed successfully",
      severity: "success",
    });
  };

  const menuActions = createEntityMenuActions({
    onRefresh: handleRefresh,
    onClearSelection: handleClear,
  });

  // Create a stable string representation of selected client IDs for dependency tracking
  const selectedClientIdsKey = useMemo(
    () => JSON.stringify(selection.selectedClientIds),
    [selection.selectedClientIds],
  );

  // Scroll into view when entities are loaded and client is selected
  useEffect(() => {
    if (hasSelectedClient && !isLoading && stepRef.current) {
      stepRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hasSelectedClient, isLoading, selectedClientIdsKey]);

  if (isLoading) {
    return (
      <StepContentBox
        title="ENTITIES"
        menuActions={menuActions}
        contentPadding={0}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <CircularProgress />
        </Box>
      </StepContentBox>
    );
  }

  if (error) {
    return (
      <StepContentBox
        title="ENTITIES"
        menuActions={menuActions}
        contentPadding={0}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <Typography variant="body1" color="error">
            Error loading entities. Please try refreshing.
          </Typography>
        </Box>
      </StepContentBox>
    );
  }

  return (
    <StepContentBox
      ref={stepRef}
      title="ENTITIES"
      menuActions={menuActions}
      contentPadding={0}
    >
      {hasSelectedClient && entities.length > 0 ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",

              overflowY: showAll ? "auto" : "visible",
            }}
          >
            {displayedEntities.map((entity) => (
              <EntityListItem
                key={entity.id}
                entity={entity}
                onToggle={handleEntitySelect}
              />
            ))}
          </Box>
          {entities.length > DEFAULT_DISPLAY_LIMIT && (
            <ShowAllToggle
              showAll={showAll}
              totalCount={entities.length}
              onToggle={handleShowAllToggle}
            />
          )}
        </Box>
      ) : (
        <EmptyEntityState
          hasSelectedClient={hasSelectedClient}
          hasEntities={entities.length > 0}
        />
      )}
    </StepContentBox>
  );
};

export default SelectEntityStep;
